/**
 * ConfirmDialog Component
 *
 * A confirmation dialog for user actions with support for destructive mode.
 * In destructive mode, colors are inverted to indicate danger.
 *
 * Features:
 * - Two-button layout (cancel + confirm)
 * - Destructive mode swaps button colors for dangerous actions
 * - Inherits accessibility from Modal component
 * - Icon support for buttons
 *
 * Ported from Vue ConfirmDialog.vue
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   isOpen={showDialog}
 *   title="Delete Booking"
 *   text="Are you sure you want to delete this booking?"
 *   confirmText="Delete"
 *   confirmIcon="delete"
 *   destructive
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowDialog(false)}
 * />
 * ```
 */

import React, { useCallback } from "react";
import { Modal } from "../Modal";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { ConfirmDialogProps } from "./types";
import styles from "./ConfirmDialog.module.css";

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title = "",
  text = "Er du sikker?",
  confirmText = "Bekreft",
  cancelText = "Avbryt",
  confirmIcon,
  cancelIcon,
  destructive = false,
  onConfirm,
  onCancel,
  className = "",
  "data-testid": testId,
}) => {
  // Handle confirm action
  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  // Handle cancel action
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  // Determine button variants based on destructive mode
  // Normal: cancel=danger, confirm=primary
  // Destructive: cancel=primary (safe), confirm=danger (dangerous)
  const cancelVariant = destructive ? "primary" : "danger";
  const confirmVariant = destructive ? "danger" : "primary";

  // Build class names
  const containerClasses = cx(styles.container, className);

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={handleCancel}
      size="sm"
      closeOnOverlayClick={false}
      data-testid={testId}
    >
      <div className={containerClasses}>
        <p className={styles.text}>{text}</p>

        <div className={styles.buttons}>
          <ActionButton
            label={cancelText}
            variant={cancelVariant}
            icon={
              cancelIcon ? (
                <Icon name={cancelIcon} size="sm" aria-hidden={true} />
              ) : undefined
            }
            iconPosition="left"
            onClick={handleCancel}
            data-testid={testId ? `${testId}-cancel` : "confirm-dialog-cancel"}
          />
          <ActionButton
            label={confirmText}
            variant={confirmVariant}
            icon={
              confirmIcon ? (
                <Icon name={confirmIcon} size="sm" aria-hidden={true} />
              ) : undefined
            }
            iconPosition="left"
            onClick={handleConfirm}
            data-testid={
              testId ? `${testId}-confirm` : "confirm-dialog-confirm"
            }
          />
        </div>
      </div>
    </Modal>
  );
};

ConfirmDialog.displayName = "ConfirmDialog";
